import { useEffect } from 'react';
import { usePortal } from '../portalAuth';

// Floating "Talk to Salvador Eyewear" AI voice button — storefront only
// (mounted from StoreLayout, never on the CRM admin app). Loads the
// self-hosted vanilla widget (public/voice-widget.js) + Daily's browser SDK,
// points it at the voice agent, and passes the signed-in buyer's portal JWT
// so registered customers get their own pricing / order status on the call.
//
// Backend: https://voice.prakaasheyewear.com  (SalvadorVoiceAgent, FastAPI +
// Pipecat + Daily/Deepgram/Groq/Cartesia). Brand colours (#191919 / #85CDE9)
// are the widget's built-in defaults and match --sf-primary / --sf-accent.

const AGENT_BASE = 'https://voice.prakaasheyewear.com';
const DAILY_JS = 'https://cdn.jsdelivr.net/npm/@daily-co/daily-js@0.92.2/dist/daily.js';
// public/voice-widget.js isn't content-hashed by CRA and is cached (browser +
// Cloudflare, 4h). Bump this whenever voice-widget.js changes so the new
// version is picked up immediately instead of after the TTL expires.
const WIDGET_JS = '/voice-widget.js?v=3';

function loadScript(src, id) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      if (existing.dataset.loaded === '1') resolve();
      else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', reject);
      }
      return;
    }
    const s = document.createElement('script');
    s.id = id;
    s.src = src;
    s.async = true;
    s.addEventListener('load', () => { s.dataset.loaded = '1'; resolve(); });
    s.addEventListener('error', reject);
    document.body.appendChild(s);
  });
}

export default function VoiceSupportWidget() {
  let portal = null;
  try { portal = usePortal(); } catch (e) { portal = null; }
  const customerId = portal?.customer?.id || null;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await loadScript(DAILY_JS, 'daily-js-sdk');
        await loadScript(WIDGET_JS, 'salvador-voice-widget');
        if (cancelled || !window.SalvadorVoice) return;
        window.SalvadorVoice.init({
          apiBase: AGENT_BASE,
          // The portal JWT the storefront already stores (portalAuth.js).
          getAuthToken: () => localStorage.getItem('sf_customer_token') || null,
          getCustomerId: () => customerId ? String(customerId) : null,
          position: 'right',
          // Sit on the SAME baseline as the WhatsApp FAB (StoreLayout:
          // right 22, bottom 22, 56px wide) and directly to its left:
          // 22 + 56 + 10px gap = 88.
          offsetBottom: 22,
          offsetSide: 88,
        });
      } catch (e) {
        // Non-fatal: the storefront just renders without the voice button.
      }
    })();

    return () => {
      cancelled = true;
      // Remove the widget's injected DOM so it doesn't linger if the
      // storefront layout unmounts (e.g. navigating into the CRM admin).
      document.getElementById('salvador-voice')?.remove();
      document.getElementById('salvador-voice-css')?.remove();
    };
  }, [customerId]);

  return null;
}
